import { useState, useEffect, useRef } from 'react';

import styled from 'styled-components';

import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

// 부정 평가 사유 칩 목록 (백엔드 ai_rating.reason — 100자 제한) //
const NEGATIVE_REASONS = ['부정확해요', '질문 의도와 달라요', '응답이 느려요', '표/데이터가 이상해요'];

export default function RatingBar(props) {
  const { rating, onRate } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isThanksVisible, setIsThanksVisible] = useState(false);

  const [selectedReason, setSelectedReason] = useState(null);
  const [commentValue, setCommentValue] = useState('');

  const ThanksTimerRef = useRef(null);

  // 언마운트 시 감사 문구 타이머 정리 //
  useEffect(() => {
    return () => {
      if (ThanksTimerRef.current) clearTimeout(ThanksTimerRef.current);
    };
  }, []);

  // 감사 문구 2초 노출 //
  function showThanks() {
    setIsThanksVisible(true);

    if (ThanksTimerRef.current) clearTimeout(ThanksTimerRef.current);
    ThanksTimerRef.current = setTimeout(() => setIsThanksVisible(false), 2000);
  }

  // 좋아요 클릭 — 즉시 저장, 패널 닫기 //
  function onClickThumbUp() {
    if (rating?.score === 1) return;

    onRate(1, null, null);
    setIsPanelOpen(false);
    showThanks();
  }

  // 별로예요 클릭 — 즉시 저장 + 사유 패널 열기 (이미 부정 평가면 저장 없이 패널만 재오픈) //
  function onClickThumbDown() {
    if (rating?.score !== -1) onRate(-1, null, null);

    setSelectedReason(rating?.score === -1 ? rating?.reason || null : null);
    setCommentValue(rating?.score === -1 ? rating?.comment || '' : '');
    setIsPanelOpen(true);
  }

  // 사유 칩 선택 (재클릭 시 해제) //
  function onClickReasonChip(reason) {
    setSelectedReason(prev => (prev === reason ? null : reason));
  }

  // 코멘트 입력 //
  function onChangeComment(e) {
    setCommentValue(e.target.value);
  }

  // 피드백 제출 — 사유/코멘트 포함 upsert //
  function onClickSubmitButton() {
    if (selectedReason === null && commentValue.trim() === '') return;

    onRate(-1, selectedReason, commentValue.trim() === '' ? null : commentValue.trim());
    setIsPanelOpen(false);
    showThanks();
  }

  // 패널 닫기 //
  function onClickCloseButton() {
    setIsPanelOpen(false);
  }

  // 엄지 아이콘 (active 시 채움 글리프, isDown이면 180도 회전) //
  function renderThumbIcon(isDown, isActive) {
    return (
      <ThumbSvg viewBox='0 0 24 24' $isDown={isDown} $color={isActive ? theme.primary : theme.subText}>
        {
          isActive ?
            <path d='M1 21h4V9H1v12zM23 10c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z' />
            :
            <path d='M9 21h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.58 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2zm0-12l4.34-4.34L12 10h9v2l-3 7H9V9zM1 9h4v12H1z' />
        }
      </ThumbSvg>
    );
  }

  const isSubmitDisabled = selectedReason === null && commentValue.trim() === '';

  return (
    <RatingWrapper>
      <IconRow>
        <IconButton onClick={onClickThumbUp} $hoverBg={theme.primaryAlpha10} title='도움이 됐어요'>
          {renderThumbIcon(false, rating?.score === 1)}
        </IconButton>
        <IconButton onClick={onClickThumbDown} $hoverBg={theme.primaryAlpha10} title='아쉬워요'>
          {renderThumbIcon(true, rating?.score === -1)}
        </IconButton>
        {
          isThanksVisible &&
          <ThanksText color={theme.subText}>피드백 감사합니다!</ThanksText>
        }
      </IconRow>
      {
        isPanelOpen &&
        <FeedbackPanel $bg={isDark ? theme.subBackground : theme.white} $border={isDark ? theme.background : '#ECECEC'}>
          <PanelHeader>
            <PanelTitle color={theme.text}>어떤 점이 아쉬웠나요?</PanelTitle>
            <CloseButton onClick={onClickCloseButton} color={theme.subText}>✕</CloseButton>
          </PanelHeader>
          <ChipRow>
            {NEGATIVE_REASONS.map((reason) => (
              <ReasonChip
                key={'RatingReason-' + reason}
                onClick={() => onClickReasonChip(reason)}
                $isSelected={selectedReason === reason}
                $primary={theme.primary}
                $selectedBg={theme.primaryAlpha10}
                $border={isDark ? theme.background : '#DFDFDF'}
                color={selectedReason === reason ? theme.primary : theme.subText}>
                {reason}
              </ReasonChip>
            ))}
          </ChipRow>
          <CommentInput
            rows={2}
            value={commentValue}
            onChange={onChangeComment}
            placeholder='자세한 의견을 들려주세요 (선택)'
            color={theme.text}
            $placeholderColor={theme.subText}
            $bg={isDark ? theme.background : '#FAFAFA'} />
          <SubmitRow>
            <SubmitButton onClick={onClickSubmitButton} $disabled={isSubmitDisabled} $bg={theme.primary} color={theme.white}>보내기</SubmitButton>
          </SubmitRow>
        </FeedbackPanel>
      }
    </RatingWrapper>
  );
}

const RatingWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  gap: 8px;
`;

const IconRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  gap: 2px;
`;

const IconButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 28px;
  height: 28px;

  border-radius: 6px;

  transition: background-color 0.2s ease-in-out;

  &:hover {
    cursor: pointer;
    background-color: ${props => props.$hoverBg};
  }
`;

const ThumbSvg = styled.svg`
  width: 16px;
  height: 16px;

  fill: ${props => props.$color};

  transform: ${props => props.$isDown ? 'rotate(180deg)' : 'none'};
`;

const ThanksText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 13px;

  color: ${props => props.color};

  margin: 0 0 0 6px;
`;

const FeedbackPanel = styled.div`
  display: flex;
  flex-direction: column;

  padding: 12px;

  width: 100%;
  max-width: 420px;

  gap: 10px;

  border: 1px solid ${props => props.$border};
  border-radius: 10px;

  box-sizing: border-box;

  background-color: ${props => props.$bg};
`;

const PanelHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const PanelTitle = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0;
`;

const CloseButton = styled.p`
  font-size: 13px;

  color: ${props => props.color};

  margin: 0;

  &:hover {
    cursor: pointer;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  gap: 6px;
`;

const ReasonChip = styled.div`
  padding: 4px 10px;

  font-family: NotoSansKR-Regular;
  font-size: 13px;

  color: ${props => props.color};

  border: 1px solid ${props => props.$isSelected ? props.$primary : props.$border};
  border-radius: 15px;

  background-color: ${props => props.$isSelected ? props.$selectedBg : 'transparent'};

  transition: all 0.2s ease-in-out;

  &:hover {
    cursor: pointer;
    border-color: ${props => props.$primary};
  }
`;

const CommentInput = styled.textarea`
  padding: 8px 10px;

  width: 100%;

  font-family: NotoSansKR-Regular;
  font-size: 13px;
  line-height: 19px;

  color: ${props => props.color};

  border: none;
  border-radius: 8px;
  outline: none;
  resize: none;

  box-sizing: border-box;

  background-color: ${props => props.$bg};

  &::placeholder {
    color: ${props => props.$placeholderColor};
  }
`;

const SubmitRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

const SubmitButton = styled.div`
  padding: 5px 14px;

  font-family: NotoSansKR-Medium;
  font-size: 13px;

  color: ${props => props.color};

  border-radius: 8px;

  background-color: ${props => props.$bg};

  opacity: ${props => props.$disabled ? 0.5 : 1};

  &:hover {
    cursor: ${props => props.$disabled ? 'default' : 'pointer'};
  }
`;
